import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    Anchor,
    ArrowRight,
    ArrowUpRight,
    Boxes,
    Cog,
    Container,
    Factory,
    Layers,
    MapPin,
    MessageCircle,
    Package,
    Truck,
} from "lucide-react";
import { MaskLine, Reveal, SectionHead, EASE } from "../components/Reveal";
import Marquee from "../components/Marquee";
import CtaBand from "../components/CtaBand";
import { DEFAULT_WA, IMAGES, TRAILERS } from "../lib/site";

const STATS = [
    { big: "2022", label: "Established — Kalamboli, Navi Mumbai" },
    { big: "40–60ft", label: "Trailer range — flatbed, semilowbed & lowbed" },
    { big: "24×7", label: "Vehicle placement on call" },
    { big: "Pan India", label: "Long-distance service network" },
];

const SERVICES = [
    {
        no: "01",
        title: "Pan India Trailer Service",
        hindi: "Trailer service across Pan India",
        desc: "Dedicated trailers on every major lane — Mumbai to UP, MP, Chhattisgarh, Odisha, Jharkhand, Bihar, West Bengal and Assam.",
        testid: "service-row-1",
    },
    {
        no: "02",
        title: "ODC & Loose Cargo Transportation",
        hindi: "ODC & loose cargo movement",
        desc: "Over-dimensional and loose cargo moved with the right trailer, route planning and care.",
        testid: "service-row-2",
    },
    {
        no: "03",
        title: "Trailer Availability",
        hindi: "Trailers available on demand",
        desc: "40ft, 50ft and 60ft trailers placed on call, across different locations in India.",
        testid: "service-row-3",
    },
    {
        no: "04",
        title: "Long-Distance Transportation",
        hindi: "Long-distance haulage",
        desc: "Inter-state haulage with committed timelines — loaded on time, delivered on time.",
        testid: "service-row-4",
    },
];

const CARGO = [
    { icon: Package, label: "ODC" },
    { icon: Cog, label: "Heavy Machinery" },
    { icon: Layers, label: "Steel" },
    { icon: Factory, label: "Industrial Equipment" },
    { icon: Boxes, label: "Loose Cargo" },
    { icon: Container, label: "Containers" },
];

function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1.07, 1]);

    return (
        <section
            ref={ref}
            className="relative overflow-hidden pb-14 pt-28 lg:pb-20 lg:pt-36"
            data-testid="hero-section"
        >
            <div className="container-x grid items-center gap-12 lg:grid-cols-12">
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: EASE }}
                        className="flex items-center gap-3"
                    >
                        <span className="relative flex h-2.5 w-2.5">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#10B981] opacity-60" />
                            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#10B981]" />
                        </span>
                        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-steel">
                            Est. 2022 — Kalamboli, Navi Mumbai
                        </p>
                    </motion.div>

                    <h1 className="mt-6 font-display font-extrabold uppercase leading-[0.95] tracking-tight">
                        <MaskLine delay={0.1} className="text-5xl text-ink sm:text-7xl lg:text-[5.6rem]">
                            OSM Cargo
                        </MaskLine>
                        <MaskLine
                            delay={0.22}
                            className="text-5xl text-brand sm:text-7xl lg:text-[5.6rem]"
                        >
                            Movers
                        </MaskLine>
                        <MaskLine
                            delay={0.34}
                            className="mt-4 font-hindi text-xl font-semibold normal-case tracking-normal text-steel sm:text-2xl"
                        >
                            Trailer Supply Across Pan India
                        </MaskLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.5, ease: EASE }}
                        className="mt-6 max-w-xl text-base leading-relaxed text-steel sm:text-lg"
                    >
                        One Stop Movers — <strong className="font-semibold text-ink">Pan India Trailer Supplier</strong> for
                        40ft, 50ft & 60ft trailers. ODC & loose cargo transportation, long-distance
                        haulage, reliable vehicle placement at competitive rates.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.62, ease: EASE }}
                        className="mt-6 flex flex-wrap items-center gap-2"
                    >
                        {["40FT", "50FT", "60FT"].map((s) => (
                            <span
                                key={s}
                                className="rounded-full border border-ink/15 bg-white px-4 py-1.5 text-xs font-bold tracking-[0.2em] text-ink"
                            >
                                {s}
                            </span>
                        ))}
                        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-fog">
                            Trailers available now
                        </span>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.74, ease: EASE }}
                        className="mt-9 flex flex-wrap gap-4"
                    >
                        <Link to="/contact#quote" data-testid="hero-quote-btn" className="btn-primary">
                            Get a Trailer Quote
                            <ArrowUpRight className="h-4 w-4" />
                        </Link>
                        <a
                            href={DEFAULT_WA}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-whatsapp-btn"
                            className="btn-whatsapp"
                        >
                            <MessageCircle className="h-4 w-4" />
                            WhatsApp Enquiry
                        </a>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
                    className="lg:col-span-5"
                >
                    <div className="relative overflow-hidden rounded-3xl" data-testid="hero-image-panel">
                        <div className="aspect-[4/5] w-full">
                            <motion.img
                                src={IMAGES.hero}
                                alt="Semi trailer truck on an Indian highway"
                                style={{ y: imgY, scale: imgScale }}
                                className="h-full w-full object-cover"
                            />
                        </div>
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
                        <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4">
                            <div>
                                <p className="text-xs font-medium text-paper/75">
                                    Trailers placed across every major corridor
                                </p>
                            </div>
                            <div className="rounded-xl border border-paper/20 bg-ink/40 px-3 py-2 backdrop-blur-sm">
                                <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-paper">
                                    <Truck className="h-3.5 w-3.5 text-brand" />
                                    40 • 50 • 60 FT
                                </p>
                            </div>
                        </div>
                    </div>
                    <p className="mt-4 flex items-center gap-2 text-xs font-bold text-steel">
                        <MapPin className="h-3.5 w-3.5 text-brand" />
                        A-4020, Bima Complex, Steel Market Road, Kalamboli, Navi Mumbai – 410218
                    </p>
                </motion.div>
            </div>
        </section>
    );
}

export default function Home() {
    return (
        <>
            <Hero />
            <Marquee />

            <section className="border-b border-line" data-testid="stats-strip">
                <div className="container-x grid grid-cols-2 gap-px overflow-hidden lg:grid-cols-4">
                    {STATS.map((s, i) => (
                        <Reveal
                            key={s.big}
                            delay={i * 0.07}
                            className={`py-10 lg:py-14 ${i < STATS.length - 1 ? "lg:border-r lg:border-line" : ""} ${i % 2 === 0 ? "border-r border-line lg:border-r" : ""}`}
                        >
                            <p className="font-display text-4xl font-extrabold tracking-tight text-ink sm:text-5xl">
                                {s.big}
                            </p>
                            <p className="mt-3 max-w-[220px] text-xs font-medium leading-relaxed text-steel">
                                {s.label}
                            </p>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="py-20 lg:py-28" data-testid="home-trailers-section">
                <div className="container-x">
                    <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
                        <SectionHead
                            eyebrow="Trailer Availability"
                            title="40FT | 50FT | 60FT"
                            hindi="Trailers available across cities — call us"
                        />
                        <Reveal delay={0.1}>
                            <Link
                                to="/trailer-availability"
                                data-testid="home-trailers-view-all"
                                className="inline-flex items-center gap-2 text-sm font-semibold text-ink underline decoration-brand decoration-2 underline-offset-4 hover:text-brand"
                            >
                                View full availability
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </Reveal>
                    </div>

                    <div className="mt-12 grid gap-6 md:grid-cols-3">
                        {TRAILERS.map((t, i) => (
                            <Reveal key={t.size} delay={i * 0.08} className="h-full">
                                <Link
                                    to="/trailer-availability"
                                    data-testid={t.testid}
                                    className="group flex h-full flex-col rounded-2xl border border-line bg-white p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-ink hover:shadow-xl hover:shadow-ink/5"
                                >
                                    <p className="font-display text-5xl font-extrabold tracking-tight text-ink group-hover:text-brand">
                                        {t.size}
                                    </p>
                                    <p className="mt-2 text-xs font-semibold uppercase tracking-[0.2em] text-fog">
                                        {t.type}
                                    </p>
                                    <p className="mt-4 font-hindi text-sm text-steel">{t.hindi}</p>
                                    <ul className="mt-5 space-y-2 border-t border-line pt-5">
                                        {t.cargo.map((c) => (
                                            <li
                                                key={c}
                                                className="flex items-center gap-2 text-sm text-steel"
                                            >
                                                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                                                {c}
                                            </li>
                                        ))}
                                    </ul>
                                    <span className="mt-auto pt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-ink">
                                        Check availability
                                        <ArrowRight className="h-4 w-4 text-brand transition-transform duration-300 group-hover:translate-x-1" />
                                    </span>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-sand py-20 lg:py-28" data-testid="home-services-section">
                <div className="container-x">
                    <SectionHead
                        eyebrow="Our Services"
                        title="What we move, where we run"
                        hindi="Our services — across all of India"
                    />
                    <div className="mt-12">
                        {SERVICES.map((s, i) => (
                            <Reveal key={s.no} delay={i * 0.05}>
                                <Link
                                    to="/services"
                                    data-testid={s.testid}
                                    className="group grid grid-cols-[auto_1fr_auto] items-center gap-5 border-t border-ink/10 py-7 transition-colors last:border-b hover:bg-white/60 sm:gap-8 sm:px-4"
                                >
                                    <span className="font-mono text-sm text-brand">{s.no}</span>
                                    <span>
                                        <span className="block font-display text-xl font-bold uppercase tracking-tight text-ink sm:text-3xl">
                                            {s.title}
                                        </span>
                                        <span className="mt-1 block text-sm text-steel sm:text-base">
                                            {s.desc}
                                        </span>
                                        <span className="mt-1 block font-hindi text-sm text-fog">
                                            {s.hindi}
                                        </span>
                                    </span>
                                    <span className="flex h-11 w-11 items-center justify-center rounded-full border border-ink/15 transition-all duration-300 group-hover:border-brand group-hover:bg-brand group-hover:text-white">
                                        <ArrowRight className="h-4 w-4" />
                                    </span>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-20 lg:py-28" data-testid="home-owner-section">
                <div className="container-x grid items-center gap-12 lg:grid-cols-12">
                    <div className="lg:col-span-4" data-testid="owner-photo-card">
                        <img
                            src="/owner-anand-dubey.jpg"
                            alt="Anand Dubey — Founder, OSM Cargo Movers"
                            loading="lazy"
                            className="aspect-[3/4] w-full rounded-3xl border border-line object-cover shadow-xl shadow-ink/10"
                        />
                        <p className="mt-4 text-center text-base font-extrabold text-ink">
                            Anand Dubey
                            <span className="block text-xs font-bold text-steel">
                                Founder & Owner — OSM Cargo Movers
                            </span>
                        </p>
                    </div>
                    <div className="lg:col-span-8">
                        <div className="border-l-4 border-brand pl-6 sm:pl-10">
                            <p className="eyebrow">Owner's Message</p>
                            <blockquote className="mt-5 font-display text-2xl font-bold leading-snug tracking-tight text-ink sm:text-4xl">
                                “Trusted transportation across every highway in India.”
                            </blockquote>
                            <p className="mt-5 text-lg text-steel">
                                Your trust, our service.
                            </p>
                        </div>
                    </div>
                    <div className="lg:col-span-12">
                        <p className="eyebrow">Cargo We Handle</p>
                        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="cargo-types-grid">
                            {CARGO.map((c) => (
                                <div
                                    key={c.label}
                                    className="flex items-center gap-3 rounded-xl border border-line bg-white px-4 py-3.5 transition-colors hover:border-brand"
                                >
                                    <c.icon className="h-4 w-4 shrink-0 text-brand" />
                                    <span className="text-sm font-semibold text-ink">
                                        {c.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <CtaBand />
        </>
    );
}
